export const MARKET_QUERY_SCHEMA_VERSION = 1;
export const MARKET_QUERY_PAGE_SIZE = 100;

export const MARKET_SELECTIONS = Object.freeze([
  'platinum',
  'compatibility',
  'ipo',
  'bullish_week',
  'bullish_month',
  'hot_week'
]);

export const MARKET_SELECTION_LABELS = Object.freeze({
  platinum: 'Platinum',
  compatibility: 'Compatibilidad',
  ipo: 'IPOs',
  bullish_week: 'Alcistas semana',
  bullish_month: 'Alcistas mes',
  hot_week: 'Más comentados'
});

export const MARKET_QUERY_FILTERS = Object.freeze([
  'currencies', 'countries', 'sectors', 'industries', 'entry_types',
  'directions', 'profitability_ranges', 'value_types', 'dividends',
  'amount', 'entry_distance', 'age', 'long_term_profitability', 'wave',
  'annual_variation', 'support_date', 'profitability_date',
  'max_min_bar_date', 'extra_cfds_direction', 'regular_values_only'
]);

export const MARKET_QUERY_SORT_FIELDS = Object.freeze([
  'original', 'ticker', 'current_price', 'entry_price', 'stop_price',
  'support_price', 'support_date', 'profitability', 'entry_type',
  'instrument_number', 'entry_distance', 'resistance', 'annual_variation',
  'wave_number', 'next_entry', 'first_trading_date', 'risk_reward',
  'compatibility'
]);

const ENUM_FILTER_VALUES = Object.freeze({
  entry_types: ['PER', 'RR', 'RRA', 'LT', 'LTA', 'SE', 'MKT'],
  directions: [0, 1],
  profitability_ranges: ['under_35', '35_75', '75_150', '150_400', 'over_400'],
  value_types: ['stock', 'etf'],
  dividends: ['0', '1', '2', '3_plus']
});

const STRING_ARRAY_FILTERS = ['currencies', 'countries', 'sectors', 'industries'];
const RANGE_FILTERS = ['amount', 'entry_distance', 'age', 'long_term_profitability', 'wave', 'annual_variation'];
const DATE_RANGE_FILTERS = ['support_date', 'profitability_date', 'max_min_bar_date'];
const BOOLEAN_FILTERS = ['extra_cfds_direction', 'regular_values_only'];

export class MarketQueryValidationError extends Error {
  constructor(fields) {
    super('La consulta de mercado no es válida.');
    this.name = 'MarketQueryValidationError';
    this.fields = fields;
  }
}

function unique(values) {
  return values.filter((value, index) => values.indexOf(value) === index);
}

function assertArray(value, field, errors) {
  if (!Array.isArray(value)) {
    errors[field] = ['Debe ser una lista.'];
    return [];
  }
  return value;
}

function normalizeStringArray(value, field, errors) {
  const values = assertArray(value, field, errors);
  const invalid = values.some(item => typeof item !== 'string');
  if (invalid) {
    errors[field] = ['Todos los valores deben ser texto.'];
  }
  return unique(values
    .filter(item => typeof item === 'string')
    .map(item => item.trim())
    .filter(item => item && item !== '0'));
}

function normalizeEnumArray(value, field, allowed, errors) {
  const values = assertArray(value, field, errors);
  const normalized = unique(values);
  if (normalized.some(item => allowed.indexOf(item) === -1)) {
    errors[field] = ['Contiene un valor no admitido.'];
  }
  return normalized.filter(item => allowed.indexOf(item) !== -1);
}

function normalizeRange(value, field, keys, errors) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    errors[field] = ['Debe ser un rango.'];
    return null;
  }
  const result = {};
  Object.keys(value).forEach(key => {
    if (keys.indexOf(key) === -1) {
      errors[field] = ['Contiene una propiedad no admitida.'];
    }
  });
  keys.forEach(key => {
    if (value[key] !== undefined && value[key] !== null && value[key] !== '') {
      result[key] = value[key];
    }
  });
  return Object.keys(result).length ? result : null;
}

export function createEmptyMarketQuery() {
  return {
    schema_version: MARKET_QUERY_SCHEMA_VERSION,
    selections: [],
    description: '',
    filters: {},
    sort: { field: 'original', direction: 'asc' },
    pagination: { offset: 0, limit: MARKET_QUERY_PAGE_SIZE }
  };
}

export function normalizeMarketQuery(input = {}) {
  const errors = {};
  const source = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  const allowedTopLevel = ['schema_version', 'selections', 'description', 'filters', 'sort', 'pagination'];
  Object.keys(source).forEach(key => {
    if (allowedTopLevel.indexOf(key) === -1) {
      errors.query = ['Contiene una propiedad no admitida.'];
    }
  });

  if (source.schema_version !== undefined && source.schema_version !== MARKET_QUERY_SCHEMA_VERSION) {
    errors.schema_version = ['Sólo se admite la versión 1.'];
  }

  const selections = normalizeEnumArray(source.selections || [], 'selections', MARKET_SELECTIONS, errors);
  const description = source.description === undefined ? '' : source.description;
  if (typeof description !== 'string' || description.trim().length > 200) {
    errors.description = ['Debe ser texto de hasta 200 caracteres.'];
  }

  const filtersSource = source.filters === undefined ? {} : source.filters;
  const filters = {};
  if (!filtersSource || typeof filtersSource !== 'object' || Array.isArray(filtersSource)) {
    errors.filters = ['Debe ser un objeto.'];
  } else {
    Object.keys(filtersSource).forEach(key => {
      if (MARKET_QUERY_FILTERS.indexOf(key) === -1) {
        errors[`filters.${key}`] = ['Filtro no admitido.'];
      }
    });
    STRING_ARRAY_FILTERS.forEach(name => {
      if (filtersSource[name] !== undefined) {
        const value = normalizeStringArray(filtersSource[name], `filters.${name}`, errors);
        if (value.length) filters[name] = value;
      }
    });
    Object.keys(ENUM_FILTER_VALUES).forEach(name => {
      if (filtersSource[name] !== undefined) {
        const value = normalizeEnumArray(filtersSource[name], `filters.${name}`, ENUM_FILTER_VALUES[name], errors);
        if (value.length) filters[name] = value;
      }
    });
    RANGE_FILTERS.forEach(name => {
      if (filtersSource[name] !== undefined) {
        const value = normalizeRange(filtersSource[name], `filters.${name}`, ['min', 'max'], errors);
        if (value) filters[name] = value;
      }
    });
    DATE_RANGE_FILTERS.forEach(name => {
      if (filtersSource[name] !== undefined) {
        const value = normalizeRange(filtersSource[name], `filters.${name}`, ['from', 'to'], errors);
        if (value) filters[name] = value;
      }
    });
    BOOLEAN_FILTERS.forEach(name => {
      if (filtersSource[name] !== undefined) {
        if (typeof filtersSource[name] !== 'boolean') {
          errors[`filters.${name}`] = ['Debe ser booleano.'];
        } else if (filtersSource[name]) {
          filters[name] = true;
        }
      }
    });
  }

  const sortSource = source.sort || {};
  const sort = {
    field: sortSource.field || 'original',
    direction: sortSource.direction || 'asc'
  };
  if (MARKET_QUERY_SORT_FIELDS.indexOf(sort.field) === -1) errors['sort.field'] = ['Orden no admitido.'];
  if (['asc', 'desc'].indexOf(sort.direction) === -1) errors['sort.direction'] = ['Dirección no admitida.'];

  const paginationSource = source.pagination || {};
  const offset = paginationSource.offset === undefined ? 0 : Number(paginationSource.offset);
  const limit = paginationSource.limit === undefined ? MARKET_QUERY_PAGE_SIZE : Number(paginationSource.limit);
  if (!Number.isInteger(offset) || offset < 0) errors['pagination.offset'] = ['Debe ser un entero positivo.'];
  if (!Number.isInteger(limit) || limit < 1 || limit > 250) errors['pagination.limit'] = ['Debe estar entre 1 y 250.'];

  if (Object.keys(errors).length) throw new MarketQueryValidationError(errors);

  return {
    schema_version: MARKET_QUERY_SCHEMA_VERSION,
    selections,
    description: typeof description === 'string' ? description.trim() : '',
    filters,
    sort,
    pagination: { offset, limit }
  };
}

export function cloneMarketQuery(query) {
  return normalizeMarketQuery(JSON.parse(JSON.stringify(query || createEmptyMarketQuery())));
}

export function firstMarketQueryPage(query) {
  const normalized = cloneMarketQuery(query);
  normalized.pagination.offset = 0;
  return normalized;
}

export function nextMarketQueryPage(query, nextOffset) {
  const normalized = cloneMarketQuery(query);
  normalized.pagination.offset = nextOffset;
  return normalized;
}

export function countActiveMarketFilters(query) {
  const normalized = normalizeMarketQuery(query);
  return normalized.selections.length + (normalized.description ? 1 : 0) + Object.keys(normalized.filters).length;
}

export function summarizeMarketQuery(query) {
  const normalized = normalizeMarketQuery(query);
  const chips = normalized.selections.map(selection => MARKET_SELECTION_LABELS[selection]);
  if (normalized.description) chips.push(`Descripción: ${normalized.description}`);
  Object.keys(normalized.filters).forEach(name => chips.push(name));
  return {
    count: countActiveMarketFilters(normalized),
    label: chips.length ? chips.join(', ') : 'Sin filtros',
    chips
  };
}
